import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { Mic2, Users, Flame, Star, ArrowRight } from "lucide-react";

const TRACKS = [
  {
    slug: "teacher-training",
    icon: Users,
    tag: "Teach",
    title: "USDD Teacher Training",
    desc: "Become a certified USDD instructor. Pedagogy, safeguarding, lesson planning and street-dance methodology.",
    duration: "12–24 weeks",
    outcomes: ["USDD Certification", "Paid teaching opportunities", "Studio placement"],
  },
  {
    slug: "professional-dancer",
    icon: Flame,
    tag: "Perform",
    title: "Professional Dancer Track",
    desc: "Full-time training for working performers — auditions, agency prep, music video and tour readiness.",
    duration: "1 Year · Full-Time",
    outcomes: ["Audition coaching", "Reel & headshots", "Agency intros"],
  },
  {
    slug: "choreographer",
    icon: Mic2,
    tag: "Create",
    title: "Choreographer Programme",
    desc: "Develop your voice as a creator — concept, composition, direction and production fundamentals.",
    duration: "20 weeks",
    outcomes: ["Original repertoire", "Showreel cut", "Industry mentor"],
  },
  {
    slug: "battle-athlete",
    icon: Star,
    tag: "Compete",
    title: "Battle Athlete Path",
    desc: "Elite training for competitive dancers across breaking, hip-hop, popping, locking and all-styles.",
    duration: "Open enrolment",
    outcomes: ["Crew placement", "Travel circuit", "Battle strategy"],
  },
];

export const CareerTracks = () => {
  return (
    <section
      id="career-tracks"
      data-testid="career-tracks-section"
      className="relative py-24 sm:py-32 overflow-hidden"
    >
      <div className="absolute top-0 left-0 w-[400px] h-[400px] rounded-full bg-brand-purple/25 blur-[150px]" />
      <div className="absolute bottom-0 right-0 w-[400px] h-[400px] rounded-full bg-brand-yellow/10 blur-[140px]" />

      <div className="relative max-w-7xl mx-auto px-6 sm:px-8">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-16">
          <div className="max-w-2xl">
            <div className="flex items-center gap-3 mb-4">
              <span className="h-px w-10 bg-brand-yellow" />
              <span className="text-xs font-bold uppercase tracking-[0.3em] text-brand-yellow">06 · Career Tracks</span>
            </div>
            <h2 className="font-display text-5xl sm:text-6xl lg:text-7xl text-white tracking-wide leading-[0.95]">
              Train to <span className="text-brand-yellow text-glow-yellow">teach</span>,
              <br />
              perform & <span className="text-brand-purpleLight">create</span>.
            </h2>
          </div>
          <p className="text-zinc-400 text-base max-w-md">
            Structured tracks for dancers who want it as a career — whether that's
            teaching the next generation, going pro on stage, or building your own legacy.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
          {TRACKS.map((t, i) => (
            <motion.div
              key={t.title}
              data-testid={`career-track-${i}`}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: i * 0.08 }}
              className="group relative p-7 sm:p-8 rounded-3xl bg-zinc-950/70 border border-white/10 hover:border-brand-yellow/50 hover:-translate-y-1 transition-all"
            >
              <div className="flex items-start justify-between mb-5">
                <div className="w-12 h-12 rounded-2xl bg-brand-yellow/10 border border-brand-yellow/30 flex items-center justify-center group-hover:bg-brand-yellow group-hover:text-black transition-all">
                  <t.icon size={22} className="text-brand-yellow group-hover:text-black transition-colors" />
                </div>
                <span className="text-[10px] uppercase tracking-[0.3em] text-brand-purpleLight font-bold px-3 py-1 border border-brand-purpleLight/40 rounded-full">
                  {t.tag}
                </span>
              </div>
              <h3 className="font-display text-3xl text-white tracking-wide">{t.title}</h3>
              <p className="mt-3 text-sm text-zinc-400 leading-relaxed">{t.desc}</p>

              <ul className="mt-5 grid grid-cols-1 sm:grid-cols-3 gap-2">
                {t.outcomes.map((o) => (
                  <li
                    key={o}
                    className="text-[11px] uppercase tracking-wider text-zinc-300 bg-white/5 border border-white/10 rounded-full px-3 py-1.5 text-center"
                  >
                    {o}
                  </li>
                ))}
              </ul>

              <div className="mt-6 pt-5 border-t border-white/10 flex items-center justify-between">
                <span className="text-[11px] uppercase tracking-[0.25em] text-zinc-500">{t.duration}</span>
                <Link
                  to={`/apply/${t.slug}`}
                  data-testid={`career-track-${t.slug}-apply`}
                  className="inline-flex items-center gap-1 text-xs font-bold uppercase tracking-wider text-brand-yellow hover:gap-2 transition-all"
                >
                  Apply <ArrowRight size={14} />
                </Link>
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};
